// The raw table component in the application tracker
// Cleaned on 9/6

import React, { useContext, useState } from 'react';
import { ApplicationTrackerContext } from '../../contexts/ApplicationTrackerContext';
import TableRow from './TableRow';

const ApplicationTrackerTable = ({ nodeRef }) => {
  const {
    timeLastScraped,
    scrapeEmailsModalOpen,
    setScrapeEmailsModalOpen,
    currentApps,
    addApplicationModalOpen,
    setAddApplicationModalOpen,
    emailScrapingAllowed,
    scrapingStatus,
  } = useContext(ApplicationTrackerContext);

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    jobFocus: 'all',
    location: 'all',
    season: 'all',
  });
  const [showFilters, setShowFilters] = useState(false);

  // Define the order in which the statuses should be sorted in the display
  const statusOrder = [
    'Live Interviewing',
    'Online Assessment',
    'Application Submitted',
    'Offer Received',
    'Rejected',
  ];

  // Get unique values for filter options
  const getUniqueValues = (apps, field) => {
    const values = apps
      .map((app) => {
        switch (field) {
          case 'status':
            return app.status;
          case 'jobFocus':
            return app.role?.job_focus || app.job_focus;
          case 'location':
            return app.role?.location || app.location;
          case 'season':
            return app.role?.internship_season || app.internship_season;
          default:
            return null;
        }
      })
      .filter(Boolean);
    return [...new Set(values)].sort();
  };

  // Filter and sort applications
  const filteredAndSortedApps = React.useMemo(() => {
    let filtered = currentApps.filter((app) => {
      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const matchesCompany = app.company_name?.toLowerCase().includes(searchTerm);
        const matchesPosition = app.position?.toLowerCase().includes(searchTerm);
        if (!matchesCompany && !matchesPosition) return false;
      }

      // Status filter
      if (filters.status !== 'all' && app.status !== filters.status) {
        return false;
      }

      // Job focus filter
      if (filters.jobFocus !== 'all') {
        const jobFocus = app.role?.job_focus || app.job_focus;
        if (jobFocus !== filters.jobFocus) return false;
      }

      // Location filter
      if (filters.location !== 'all') {
        const location = app.role?.location || app.location;
        if (location !== filters.location) return false;
      }

      // Season filter
      if (filters.season !== 'all') {
        const season = app.role?.internship_season || app.internship_season;
        if (season !== filters.season) return false;
      }

      return true;
    });

    // Sort the filtered applications
    return filtered.sort((a, b) => {
      return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
    });
  }, [currentApps, filters, statusOrder]);

  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      jobFocus: 'all',
      location: 'all',
      season: 'all',
    });
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="max-w-full px-4 sm:px-6 lg:px-8 mx-auto">
      <div className="-m-1.5 w-full">
        <div className="p-1.5 w-full inline-block align-middle">
          <div
            className={`flex flex-col border border-gray-200 rounded-xl shadow-xs bg-white w-full ${currentApps.length > 0 ? 'flex-1' : 'h-auto'}`}
          >
            {/* Header */}
            <div className="px-6 py-4 grid gap-3 md:flex md:justify-between md:items-center border-b border-gray-200">
              {/* Search Input */}
              <div className="sm:col-span-2 md:flex-1 md:max-w-sm lg:max-w-md">
                <label htmlFor="application-search" className="sr-only">
                  Search
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="application-search"
                    name="application-search"
                    className="py-2 px-3 ps-11 block w-full border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                    placeholder="Search applications..."
                    value={filters.search}
                    onChange={(e) => updateFilter('search', e.target.value)}
                  />
                  <div className="absolute inset-y-0 inset-s-0 flex items-center pointer-events-none ps-4">
                    <svg
                      className="shrink-0 size-4 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.3-4.3" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="sm:col-span-1 md:flex md:items-center md:justify-end">
                <div className="flex justify-end gap-x-2">
                  {/* Email scraping button */}
                  {emailScrapingAllowed && (
                    <button
                      type="button"
                      className="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none relative"
                      onClick={() => setScrapeEmailsModalOpen(true)}
                      title={
                        scrapingStatus?.is_initial_scrape_complete
                          ? 'Automated scraping is active - runs hourly'
                          : 'Set up automated email scraping to track applications'
                      }
                    >
                      {scrapingStatus?.is_initial_scrape_complete ? (
                        <>
                          <svg
                            className="shrink-0 size-3.5"
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                          Email Status
                          {/* Green dot indicator for active automation */}
                          <span className="absolute -top-1 -right-1 flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                          </span>
                        </>
                      ) : (
                        <>
                          <svg
                            className="shrink-0 size-3.5"
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                            <polyline points="22,6 12,13 2,6" />
                          </svg>
                          Set Up Email Automation
                        </>
                      )}
                    </button>
                  )}

                  {/* Filter Dropdown */}
                  <div
                    className="hs-dropdown [--placement:bottom-right] relative inline-block"
                    data-hs-dropdown-auto-close="inside"
                  >
                    <button
                      id="application-filter-dropdown"
                      type="button"
                      className={`py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border shadow-xs hover:bg-gray-50 focus:outline-hidden disabled:opacity-50 disabled:pointer-events-none transition-colors ${
                        showFilters
                          ? 'border-blue-200 bg-blue-50 text-blue-800 focus:bg-blue-50'
                          : 'border-gray-200 bg-white text-gray-800 focus:bg-gray-50'
                      }`}
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <svg
                        className={`shrink-0 size-3.5 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`}
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 6h18" />
                        <path d="M7 12h10" />
                        <path d="M10 18h4" />
                      </svg>
                      Filter
                      {(filters.search ||
                        filters.status !== 'all' ||
                        filters.jobFocus !== 'all' ||
                        filters.location !== 'all' ||
                        filters.season !== 'all') && (
                        <span className="ps-2 text-xs font-semibold text-blue-600 border-s border-gray-200">
                          {
                            [
                              filters.search && 1,
                              filters.status !== 'all' && 1,
                              filters.jobFocus !== 'all' && 1,
                              filters.location !== 'all' && 1,
                              filters.season !== 'all' && 1,
                            ].filter(Boolean).length
                          }
                        </span>
                      )}
                    </button>
                  </div>

                  {/* Add Application Button */}
                  <button
                    ref={nodeRef}
                    type="button"
                    className="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 focus:outline-hidden focus:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
                    onClick={() => setAddApplicationModalOpen(true)}
                  >
                    <svg
                      className="shrink-0 size-3.5"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12h14" />
                      <path d="M12 5v14" />
                    </svg>
                    Add Application
                  </button>
                </div>
              </div>
            </div>
            {/* End Header */}

            {/* Filters Collapsible Section */}
            {showFilters && (
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-800">Filter Options</h3>
                  <button
                    onClick={clearFilters}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Clear all filters
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Status Filter */}
                  <div>
                    <label
                      htmlFor="status-filter"
                      className="block text-xs font-medium text-gray-700 mb-1"
                    >
                      Status
                    </label>
                    <select
                      id="status-filter"
                      value={filters.status}
                      onChange={(e) => updateFilter('status', e.target.value)}
                      className="py-2 px-3 pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="all">All statuses</option>
                      {getUniqueValues(currentApps, 'status').map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Job Focus Filter */}
                  <div>
                    <label
                      htmlFor="job-focus-filter"
                      className="block text-xs font-medium text-gray-700 mb-1"
                    >
                      Job Focus
                    </label>
                    <select
                      id="job-focus-filter"
                      value={filters.jobFocus}
                      onChange={(e) => updateFilter('jobFocus', e.target.value)}
                      className="py-2 px-3 pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="all">All focuses</option>
                      {getUniqueValues(currentApps, 'jobFocus').map((focus) => (
                        <option key={focus} value={focus}>
                          {focus}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Location Filter */}
                  <div>
                    <label
                      htmlFor="location-filter"
                      className="block text-xs font-medium text-gray-700 mb-1"
                    >
                      Location
                    </label>
                    <select
                      id="location-filter"
                      value={filters.location}
                      onChange={(e) => updateFilter('location', e.target.value)}
                      className="py-2 px-3 pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="all">All locations</option>
                      {getUniqueValues(currentApps, 'location').map((location) => (
                        <option key={location} value={location}>
                          {location}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Season Filter */}
                  <div>
                    <label
                      htmlFor="season-filter"
                      className="block text-xs font-medium text-gray-700 mb-1"
                    >
                      Season
                    </label>
                    <select
                      id="season-filter"
                      value={filters.season}
                      onChange={(e) => updateFilter('season', e.target.value)}
                      className="py-2 px-3 pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="all">All seasons</option>
                      {getUniqueValues(currentApps, 'season').map((season) => (
                        <option key={season} value={season}>
                          {season}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Active Filters Display */}
                {(filters.search ||
                  filters.status !== 'all' ||
                  filters.jobFocus !== 'all' ||
                  filters.location !== 'all' ||
                  filters.season !== 'all') && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {filters.search && (
                      <span className="inline-flex items-center gap-x-1 py-1 px-2 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Search: "{filters.search}"
                        <button
                          onClick={() => updateFilter('search', '')}
                          className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </span>
                    )}
                    {filters.status !== 'all' && (
                      <span className="inline-flex items-center gap-x-1 py-1 px-2 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Status: {filters.status}
                        <button
                          onClick={() => updateFilter('status', 'all')}
                          className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-green-200"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </span>
                    )}
                    {filters.jobFocus !== 'all' && (
                      <span className="inline-flex items-center gap-x-1 py-1 px-2 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Focus: {filters.jobFocus}
                        <button
                          onClick={() => updateFilter('jobFocus', 'all')}
                          className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-purple-200"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </span>
                    )}
                    {filters.location !== 'all' && (
                      <span className="inline-flex items-center gap-x-1 py-1 px-2 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Location: {filters.location}
                        <button
                          onClick={() => updateFilter('location', 'all')}
                          className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-yellow-200"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </span>
                    )}
                    {filters.season !== 'all' && (
                      <span className="inline-flex items-center gap-x-1 py-1 px-2 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                        Season: {filters.season}
                        <button
                          onClick={() => updateFilter('season', 'all')}
                          className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-pink-200"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Table */}
            <div
              className={`${currentApps.length > 5 ? 'overflow-y-auto' : 'overflow-visible'} overflow-x-auto flex-1 w-full`}
            >
              <table className="min-w-full divide-y divide-gray-200">
                {/* Table header */}
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-start w-1/5">
                      <div className="flex items-center gap-x-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-800">
                          Company
                        </span>
                      </div>
                    </th>

                    <th scope="col" className="px-6 py-3 text-start w-1/5">
                      <div className="flex items-center gap-x-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-800">
                          Position
                        </span>
                      </div>
                    </th>

                    <th scope="col" className="px-6 py-3 text-start w-1/6">
                      <div className="flex items-center gap-x-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-800">
                          Location
                        </span>
                      </div>
                    </th>

                    <th scope="col" className="px-6 py-3 text-start w-1/6">
                      <div className="flex items-center gap-x-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-800">
                          Season
                        </span>
                      </div>
                    </th>

                    <th scope="col" className="px-6 py-3 text-start w-1/4">
                      <div className="flex items-center gap-x-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-800">
                          Status
                        </span>
                      </div>
                    </th>

                    <th scope="col" className="px-6 py-3 text-center w-16">
                      <div className="flex items-center justify-center gap-x-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-800">
                          Actions
                        </span>
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 bg-white">
                  {/* Each application is a TableRow */}
                  {filteredAndSortedApps.map((item) => (
                    <TableRow
                      key={`${item.application_id}-${item.role_id}`}
                      isHistory={false}
                      roleID={item.role_id}
                      applicationID={item.application_id}
                      date={item.date ? item.date : ''}
                      company={item.company_name}
                      status={item.status}
                      item={item}
                      position={item.position}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {/* End Table */}

            {/* Footer */}
            <div className="px-6 py-4 grid gap-3 md:flex md:justify-between md:items-center border-t border-gray-200 bg-white rounded-b-xl">
              <div>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-800">
                    {filteredAndSortedApps.length}
                  </span>{' '}
                  {filteredAndSortedApps.length !== currentApps.length && (
                    <>of {currentApps.length} </>
                  )}
                  application{filteredAndSortedApps.length !== 1 ? 's' : ''}
                  {filteredAndSortedApps.length !== currentApps.length && ' shown'}
                </p>
              </div>

              {/* Pagination would go here if needed */}
            </div>
            {/* End Footer */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationTrackerTable;
